import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  styled,
  Grid2,
} from "@mui/material";

import { useTranslation } from "react-i18next";
import HierarchicalSelector from "../Common/HierarchicalSelector";
import { fetchDigitalTwinHierarchy, getHierarchyEntityTypes } from "../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractEntities, extractUniqueLevels, fetchDigitalTwinHierarchyLevelSignals, transformTree } from "../Common/CommonFunctions";
import DuplicateSignal from "../Common/DuplicateSignal";
import { usePagesContent } from "../../../Hooks/usePagesContent";
import YColumnsTooltip from "../Common/YColumnsTooltip";

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontSize: "12px",
  lineHeight: "20px",
  fontWeight: 500,
}));

const CustomTabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: "calc(100% - 50px)" }}
        >
        {value === index && <Box sx={{ p: 3, height: '-webkit-fill-available' }}>{children}</Box>}
        </div>
    );
}

const VisualizationSettings = ({ entities, batteryID, onEntitiesChange }) => {

  const {t} = useTranslation(['common', 'digital_twin']);
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [selectedChips, setSelectedChips] = useState([]);
  const [hierarchyInputData, setHierarchyInputData] = useState({});
  const [showDuplicateError, setShowDuplicateError] = useState(false);
  const [isLoadingSignals, setIsLoadingSignals] = useState(false);
  const pageContent = usePagesContent();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSelectionChange = (chips) => {
    const extractedEntities = extractEntities(batteryID, chips, hierarchyInputData);
    setSelectedEntities(extractedEntities);
  };

  const setupDigitalTwinHierarchy = useCallback(async () => {
    setIsLoadingSignals(true);
    try {
      const entityTypes = getHierarchyEntityTypes(pageContent);
      const digitalTwinHierarchyResponse = await fetchDigitalTwinHierarchy(batteryID, entityTypes, t);
      // fetch signals for unique entities
      const uniqueLevels = extractUniqueLevels(digitalTwinHierarchyResponse ?? []);
      const digitalTwinLevelSignals = await fetchDigitalTwinHierarchyLevelSignals(batteryID, uniqueLevels, 'BINARY');
      const transformedHierarchy = transformTree(digitalTwinHierarchyResponse ?? [], digitalTwinLevelSignals);
      setHierarchyInputData(transformedHierarchy);
    } finally {
      setIsLoadingSignals(false);
    }
  }, [batteryID, t]);

  useEffect(() => {
    setupDigitalTwinHierarchy();
  }, []);

  useEffect(() => {
    // Notify parent when entities change
    onEntitiesChange(selectedEntities);
    const chips = selectedEntities.map(entity => entity.raw);
    setSelectedChips(chips);
  }, [selectedEntities, onEntitiesChange]);

  useEffect(() => {
    if ((entities || []).length) {
      setSelectedEntities(entities);
    }
  }, [entities]);

  return (
    <Box sx={{ width: '350px', borderRadius: '12px', backgroundColor: '#FFFFFF'}}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="signals-series-tab">
          <CustomTab label="Signals" id="binary-signals" ariaControls="signals" />
        </Tabs>
      </Box>
      <CustomTabPanel value={activeTab} index={0}>
        <Grid2 container flexDirection={'column'} justifyContent={'space-between'} sx={{height: 'inherit', position: 'relative'}}>
          <Grid2 container gap={0.5} flexDirection={'column'}>
            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
              <Grid2 container gap={0.5} alignItems={'center'}>
                <Typography variant="fw400s12c494949" sx={{color: '#15191E', lineHeight: '24px'}}>Y-Columns</Typography>
                <YColumnsTooltip />
              </Grid2>
              {/* <Typography variant="body2">Input Guide</Typography> */}
          </Box>
          <HierarchicalSelector
            data={hierarchyInputData}
            selectedChips={selectedChips}
            onSelectionChange={handleSelectionChange}
            onDuplicateError={() => setShowDuplicateError(true)}
            placeholder="Search attributes..."
            allowDuplicates={false}
            selectionMode="checklist"
            className="w-full"
            maxHeight={'250px'}
            isLoading={isLoadingSignals}
          />
          </Grid2>
          {showDuplicateError && <DuplicateSignal onClose={() => setShowDuplicateError(false)} />}
        </Grid2>
      </CustomTabPanel>
    </Box>
  );
};

export default VisualizationSettings;
